import { useState } from "react";
import {
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  UserRound,
} from "lucide-react";
import {
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  getStoredUser,
  isAuthenticated,
  loginUser,
} from "../../utils/auth";

function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const storedUser = getStoredUser();

  if (isAuthenticated() && storedUser) {
    return <Navigate to="/admin" replace />;
  }

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!formData.username.trim() || !formData.password) {
      setError("Enter your username and password.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await loginUser(formData);

      const destination =
        location.state?.from &&
        location.state.from.startsWith("/admin")
          ? location.state.from
          : "/admin";

      navigate(destination, {
        replace: true,
      });
    } catch (requestError) {
      setError(
        requestError.message ||
          "We could not sign you in.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="sugalump-login">
      <section className="sugalump-login__visual">
        <div className="sugalump-login__visual-content">
          <div className="sugalump-login__brand-mark">
            SL
          </div>

          <p className="eyebrow">SugaLumps Cupcakery</p>

          <h1>
            Manage every sweet detail in one place.
          </h1>

          <p>
            Review orders, manage the menu, organize
            deliveries, and keep the bakery running smoothly.
          </p>

          <div className="sugalump-login__decoration">
            <span />
            <strong>Made with love</strong>
            <span />
          </div>
        </div>
      </section>

      <section className="sugalump-login__form-side">
        <div className="sugalump-login-card">
          <header className="sugalump-login-card__header">
            <div className="sugalump-login-card__mobile-logo">
              SL
            </div>

            <p className="eyebrow">Bakery administration</p>
            <h2>Welcome back</h2>

            <p>
              Sign in to manage SugaLumps Cupcakery.
            </p>
          </header>

          {error && (
            <div
              className="sugalump-login-error"
              role="alert"
            >
              <LockKeyhole size={18} />
              <span>{error}</span>
            </div>
          )}

          <form
            className="sugalump-login-form"
            onSubmit={handleSubmit}
          >
            <div className="form-group">
              <label
                className="form-label"
                htmlFor="username"
              >
                Username
              </label>

              <div className="sugalump-login-input">
                <UserRound size={18} />

                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="sugalump-login-label-row">
                <label
                  className="form-label"
                  htmlFor="password"
                >
                  Password
                </label>
              </div>

              <div className="sugalump-login-input">
                <LockKeyhole size={18} />

                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  disabled={isSubmitting}
                />

                <button
                  type="button"
                  className="sugalump-login-password-toggle"
                  aria-label={
                    showPassword
                      ? "Hide password"
                      : "Show password"
                  }
                  onClick={() =>
                    setShowPassword((current) => !current)
                  }
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn--primary sugalump-login-submit"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <LoaderCircle
                  size={18}
                  className="admin-spin"
                />
              )}

              {isSubmitting
                ? "Signing in..."
                : "Sign In"}
            </button>
          </form>

          <footer className="sugalump-login-card__footer">
            <p>
              Authorized SugarLumps personnel only.
            </p>
          </footer>
        </div>
      </section>
    </main>
  );
}

export default AdminLoginPage;